import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRemoveCartItemMutation } from "../Services/OrderService";
import {
  addToCart,
  setCartItemCount,
  setTotalPrice,
} from "../Redux/Slices/AddToCartSlice";
import { showSnackbar } from "../Redux/Slices/NotificationSlice";
import CartUtils from "../Utils/CalculateCartPrice";

const FLEXBUS_TIMER_DURATION = 600; // 10 minutes in seconds
const CHECK_INTERVAL = 5000; // Check every 5 seconds

const useFlexbusTimerCleanup = () => {
  const dispatch = useDispatch();
  const myCartItems = useSelector((state: any) => state.AddToCartSlice);
  const authData = useSelector((state: any) => state.AuthSlice);
  const [removeCartItemTrigger] = useRemoveCartItemMutation();
  const removingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const checkExpiredTimers = async () => {
      const cart: any[] = myCartItems?.cart || [];
      const travelItems = cart.filter(
        (item: any) => item.productType === "travel"
      );

      if (travelItems.length === 0) return;

      for (const item of travelItems) {
        if (!item._id || removingRef.current.has(item._id)) continue;
        if (!item.timerStartedAt) continue;

        const startTime = new Date(item.timerStartedAt).getTime();
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = FLEXBUS_TIMER_DURATION - elapsed;

        if (remaining <= 0) {
          removingRef.current.add(item._id);

          try {
            if (authData?.isAuthenticated && item._id) {
              await removeCartItemTrigger(item._id);
            }

            const updatedCart = cart.filter(
              (_item: any) => _item._id !== item._id
            );

            dispatch(addToCart(updatedCart));
            dispatch(
              setCartItemCount(
                updatedCart.reduce(
                  (acc: any, i: any) => acc + i.quantity,
                  0
                )
              )
            );
            dispatch(
              setTotalPrice(CartUtils.calculateCartPrice(updatedCart))
            );

            dispatch(
              showSnackbar({
                message: `FlexBus ticket "${item.productName}" expired and was removed from cart`,
                severity: "warning",
              })
            );
          } catch (error) {
            dispatch(
              showSnackbar({
                message: "Failed to remove expired FlexBus ticket",
                severity: "error",
              })
            );
          } finally {
            removingRef.current.delete(item._id);
          }
        }
      }
    };

    // Run immediately on mount
    checkExpiredTimers();

    // Then check periodically
    const interval = setInterval(checkExpiredTimers, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [myCartItems?.cart, authData?.isAuthenticated]);
};

export default useFlexbusTimerCleanup;
